import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button, Typography, Spinner, Badge, Modal } from "stories/components";
import { useNotification } from "stories/components/Notification";

import PriceSourceModal from "components/PriceSource/PriceSourceModal";
import {
  httpGetPrivateInvestmentFund,
  httpSetManualPricing,
} from "services/private_investment_funds";

// How long to keep asking whether the backfill has produced prices before telling the user it
// continues in the background. A Nordic equity takes a few seconds; a US one with decades of
// history can take longer than anyone wants to watch a spinner.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60000;

/**
 * The "Kurssilähde" section of the security form.
 *
 * A security is in one of three states, and the whole point of showing them apart is that
 * "priced by hand on purpose" and "nobody ever bound a source" used to look identical — the
 * second one silently left the acquisition price on screen as if it were a market value.
 */
const PriceSourceSection = ({ objectId, values, onFundUpdated }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [fund, setFund] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollTimer = useRef(null);

  const isin = values?.isin;
  const status = fund?.price_source_status || "unset";

  const loadFund = useCallback(() => {
    if (!objectId) return Promise.resolve(null);
    return httpGetPrivateInvestmentFund(objectId).then(res => {
      setFund(res?.data);
      return res?.data;
    }, () => null);
  }, [objectId]);

  useEffect(() => { loadFund() }, [loadFund]);
  useEffect(() => () => { if (pollTimer.current) clearInterval(pollTimer.current) }, []);

  const startPolling = () => {
    setPolling(true);
    const startedAt = Date.now();
    pollTimer.current = setInterval(() => {
      loadFund().then(updated => {
        // latest_data is the 7 most recent rows, newest first — not a single object.
        const done = updated?.latest_data?.[0]?.date || updated?.price_source_error;
        if (done || Date.now() - startedAt > POLL_TIMEOUT_MS) {
          clearInterval(pollTimer.current);
          setPolling(false);
        }
      });
    }, POLL_INTERVAL_MS);
  };

  const handleBound = (updated) => {
    setModalOpen(false);
    setFund(updated);
    if (onFundUpdated) onFundUpdated(updated);
    notify({
      title: t("price_source_title", "Kurssilähde"),
      message: t("price_source_bound", "Kurssilähde otettu käyttöön. Haetaan kurssihistoriaa…"),
    });
    startPolling();
  };

  const handleManual = (manual) => {
    httpSetManualPricing(objectId, { manual_pricing: manual }).then(res => {
      setFund(res?.data);
      if (onFundUpdated) onFundUpdated(res?.data);
    });
  };

  if (!objectId) {
    return (
      <div className="mb-3 p-3 border rounded">
        <Typography bold className="d-block mb-1">
          {t("price_source_title", "Kurssilähde")}
        </Typography>
        <Typography variant="small" className="text-muted">
          {t("price_source_after_save",
             "Kurssilähde valitaan tallennuksen jälkeen, kun arvopaperi on olemassa.")}
        </Typography>
      </div>
    );
  }

  return (
    <>
      <div className="mb-3 p-3 border rounded">
        <Typography bold className="d-block mb-2">
          {t("price_source_title", "Kurssilähde")}
        </Typography>

        {polling && (
          <div className="d-flex align-items-center mb-2">
            <Spinner />
            <Typography className="ml-2">
              {t("price_source_fetching", "Haetaan kurssihistoriaa…")}
            </Typography>
          </div>
        )}

        {status === "linked" && (
          <>
            <Badge color="success" className="mb-2">
              {t("price_source_linked", "Päivittyy automaattisesti")}
            </Badge>
            <Typography variant="small" className="d-block">
              {fund?.source === "infront" ? "Infront" : "FMP"} · {fund?.symbol} · {fund?.stock_market} · {fund?.currency_label}
            </Typography>
            {fund?.latest_data?.[0]?.date && (
              <Typography variant="small" className="text-muted d-block">
                {t("price_source_latest", "Viimeisin kurssi")} {fund.latest_data[0].price} {fund.currency_label} ({fund.latest_data[0].date?.substring(0, 10)})
              </Typography>
            )}
            {fund?.price_source_error && (
              <Typography variant="small" className="text-danger d-block">
                {t("price_source_error", "Haku epäonnistui")}: {fund.price_source_error}
              </Typography>
            )}
            <button type="button" className="sk-form-create-link mt-2"
                    onClick={() => setModalOpen(true)}>
              {t("price_source_change", "Vaihda lähde")}
            </button>
          </>
        )}

        {status === "manual" && (
          <>
            <Badge color="secondary" className="mb-2">
              {t("price_source_manual", "Kurssit kirjataan itse")}
            </Badge>
            <Typography variant="small" className="text-muted d-block mb-2">
              {t("price_source_manual_info", "Automaattista hakua ei tehdä.")}
            </Typography>
            <Button variant="secondary" size="sm" disabled={!isin}
                    onClick={() => setModalOpen(true)}>
              {t("price_source_find", "Hae kurssilähde")}
            </Button>
          </>
        )}

        {status === "unset" && (
          <>
            <Badge color="warning" className="mb-2">
              {t("price_source_unset", "Kurssilähdettä ei ole valittu")}
            </Badge>
            <Typography variant="small" className="text-muted d-block mb-2">
              {t("price_source_unset_info",
                 "Kursseja ei haeta automaattisesti, ja salkussa näkyy hankintahinta markkinahinnan sijaan.")}
            </Typography>
            <div className="d-flex align-items-center">
              <Button variant="primary" size="sm" disabled={!isin}
                      onClick={() => setModalOpen(true)}>
                {t("price_source_find", "Hae kurssilähde")}
              </Button>
              <button type="button" className="sk-form-create-link ml-3"
                      onClick={() => handleManual(true)}>
                {t("price_source_set_manual", "Hoidan kurssit itse")}
              </button>
            </div>
            {!isin && (
              <Typography variant="small" className="text-muted d-block mt-2">
                {t("price_source_needs_isin", "Syötä ISIN, niin voimme etsiä kurssilähteen.")}
              </Typography>
            )}
          </>
        )}
      </div>

      <Modal
        title={t("price_source_find", "Hae kurssilähde")}
        isOpen={modalOpen}
        toggleModal={() => setModalOpen(!modalOpen)}
        size="lg"
      >
        {modalOpen && (
          <PriceSourceModal
            fund={{ ...fund, isin: isin || fund?.isin }}
            onBound={handleBound}
            onCancel={() => setModalOpen(false)}
          />
        )}
      </Modal>
    </>
  );
};

export default PriceSourceSection;
